"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getLocalizedPath } from "@/lib/localizedRoutes";
import {
    CalendarDays,
    Check,
    Gamepad2,
    Phone,
    QrCode,
    Shield,
    X,
} from "lucide-react";
import MainMenu from "@/components/navigation/MainMenu";
import StrikeAuthMenu from "@/components/auth/StrikeAuthMenu";
import { useUser } from "@/components/UserContext";
import { useLanguage } from "@/components/LanguageContext";
import { brandConfig } from "@/lib/brandConfig";

export default function Header({
    showBookingButton = true,
    showPhoneButton = !showBookingButton,
}) {
    const pathname = usePathname();
    const isBookingFlow = /^\/(?:en\/)?(?:booking|private-room\/booking)(?:\/|$)/.test(pathname || "");

    const brandStyle = {
        "--brand-primary":
            brandConfig.colors.primary,

        "--brand-primary-hover":
            brandConfig.colors.primaryHover,

        "--brand-background":
            brandConfig.colors.background,

        "--brand-text-on-primary":
            brandConfig.colors.textOnPrimary,
    };

    const [
        isMenuOpen,
        setIsMenuOpen,
    ] =
        useState(
            false
        );

    const [
        isLanguageOpen,
        setIsLanguageOpen,
    ] =
        useState(
            false
        );

    const [
        isGameServerAvailable,
        setIsGameServerAvailable,
    ] = useState(false);

    const {
        user,
        isUserLoading,
    } =
        useUser();

    const {
        language,
        setLanguage,
        isReady:
            isLanguageReady,
    } =
        useLanguage();

    const bookingButtonText =
        language === "hu"
            ? "foglalj most"
            : "book now";

    const languageTitle =
        language === "hu"
            ? "nyelv kiválasztása"
            : "choose language";

    const languageSubtitle =
        language === "hu"
            ? "válaszd ki, milyen nyelven szeretnéd használni az oldalt."
            : "choose the language you'd like to use on the site.";

    const hungarianLabel =
        "magyar";

    const englishLabel =
        "english";

    const isReceptionist =
        user
            ?.isReceptionist ===
        true;

    const isAdmin =
        user
            ?.isAdmin ===
        true;

    useEffect(() => {
        if (
            isUserLoading ||
            !isReceptionist
        ) {
            return;
        }

        const controller =
            new AbortController();

        const timeoutId =
            window.setTimeout(
                () => {
                    controller.abort();
                },
                2000
            );

        const checkGameServer = async () => {
            try {
                await fetch(
                    "http://192.168.254.5:8080/admin",
                    {
                        method: "GET",
                        cache: "no-store",
                        signal:
                            controller.signal,
                    }
                );

                setIsGameServerAvailable(
                    true
                );
            } catch (error) {
                setIsGameServerAvailable(
                    false
                );
            } finally {
                window.clearTimeout(
                    timeoutId
                );
            }
        };

        checkGameServer();

        return () => {
            window.clearTimeout(
                timeoutId
            );

            controller.abort();
        };
    }, [
        isUserLoading,
        isReceptionist,
    ]);

    useEffect(() => {
        if (!isLanguageOpen) {
            return;
        }

        const handleKeyDown = (
            event
        ) => {
            if (
                event.key ===
                "Escape"
            ) {
                setIsLanguageOpen(
                    false
                );
            }
        };

        document.addEventListener(
            "keydown",
            handleKeyDown
        );

        const previousOverflow =
            document.body.style
                .overflow;

        document.body.style.overflow =
            "hidden";

        return () => {
            document.removeEventListener(
                "keydown",
                handleKeyDown
            );

            document.body.style.overflow =
                previousOverflow;
        };
    }, [
        isLanguageOpen,
    ]);

    const closeMenu = () => {
        setIsMenuOpen(
            false
        );
    };

    const openLanguageMenu = () => {
        if (!isLanguageReady) {
            return;
        }

        closeMenu();

        setIsLanguageOpen(
            true
        );
    };

    const selectLanguage = (
        nextLanguage
    ) => {
        setLanguage(
            nextLanguage
        );

        setIsLanguageOpen(
            false
        );
    };

    return (
        <>
            <header
                style={brandStyle}
                className="relative z-[100] bg-black text-white md:fixed md:left-0 md:top-0 md:w-full"
            >
                <div
                    className="
                        mx-auto flex h-[106px]
                        w-full
                        items-center justify-between
                        gap-2 px-3 min-[390px]:gap-3 min-[390px]:px-4
                        sm:px-6
                        md:h-[120px]
                        md:max-w-6xl
                        md:gap-6
                        md:px-12
                    "
                >
                    <div className="flex shrink-0 items-center gap-1 min-[390px]:gap-2 md:gap-3">
                        <Link href={getLocalizedPath("/", language)} onClick={closeMenu}
                            aria-label={language === "en" ? "strike home" : "strike főoldal"}
                            className="flex h-12 w-12 shrink-0 items-center">
                            <img src="/v4/strike-urban-playground-logo-white.png" alt="STRIKE"
                                className="h-auto max-h-12 w-full object-contain" />
                        </Link>
                        {showPhoneButton && !isBookingFlow && !isUserLoading && !isAdmin && !isReceptionist && (
                            <a href="tel:+36301202326" onClick={closeMenu}
                                aria-label={language === "en" ? "call us" : "telefonhívás"}
                                title="+36 30 120 2326"
                                className="flex h-11 w-11 shrink-0 items-center justify-center text-white transition hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:scale-95">
                                <Phone className="h-6 w-6" strokeWidth={2.5} aria-hidden="true" />
                            </a>
                        )}

                        {showBookingButton && !isBookingFlow && (
                            <Link
                                href="/booking"
                                onClick={
                                    closeMenu
                                }
                                aria-label={
                                    bookingButtonText
                                }
                                title={
                                    bookingButtonText
                                }
                                className={`
                                    hidden h-9
                                    shrink-0 md:flex
                                    cursor-pointer
                                    items-center justify-center
                                    overflow-hidden
                                    rounded-[6px]
                                    bg-[var(--brand-primary)]
                                    text-[16px]
                                    font-black
                                    text-[var(--brand-text-on-primary)]
                                    transition-transform
                                    duration-200
                                    ease-out
                                    hover:scale-[1.02]
                                    active:translate-y-[2px]
                                    active:scale-95
                                    select-none
                                    [-webkit-touch-callout:none]
                                    md:min-w-[140px]
                                    md:px-4
                                    md:hover:scale-105
                                    button-pulse
                                    ${isReceptionist
                                        ? "w-[48px] px-0 md:w-auto"
                                        : "w-auto px-4"
                                    }
                                `}
                            >
                                {isReceptionist ? (
                                    <>
                                        <CalendarDays
                                            className="
                                                h-5 w-5
                                                md:hidden
                                            "
                                        />

                                        <span
                                            className="
                                                hidden
                                                whitespace-nowrap
                                                md:block
                                            "
                                        >
                                            {bookingButtonText}
                                        </span>
                                    </>
                                ) : (
                                    <span
                                        className="
                                            block min-w-0
                                            truncate
                                            whitespace-nowrap
                                        "
                                    >
                                        {bookingButtonText}
                                    </span>
                                )}
                            </Link>
                        )}
                    </div>

                    <div
                        className="
                            flex shrink-0
                            items-center
                            gap-1 min-[390px]:gap-2
                            md:gap-4
                        "
                    >
                        <div
                            className="
                                flex items-center
                                gap-1 min-[390px]:gap-4
                                md:gap-4
                            "
                        >
                            {!isUserLoading &&
                                isAdmin && (
                                    <Link
                                        href="/admin"
                                        onClick={
                                            closeMenu
                                        }
                                        aria-label="admin felület"
                                        title="admin felület"
                                        className="
                                            flex h-10 w-10
                                            shrink-0
                                            cursor-pointer
                                            items-center
                                            justify-center
                                            rounded-full
                                            border-2
                                            border-[var(--brand-primary)]
                                            text-[var(--brand-primary)]
                                            transition
                                            duration-200
                                            hover:bg-[var(--brand-primary)]
                                            hover:text-[var(--brand-text-on-primary)]
                                            active:scale-95
                                            sm:h-11 sm:w-11
                                            md:h-12 md:w-12
                                        "
                                    >
                                        <Shield
                                            className="
                                                h-4 w-4
                                                sm:h-5 sm:w-5
                                                md:h-6 md:w-6
                                            "
                                        />
                                    </Link>
                                )}

                            {!isUserLoading &&
                                isReceptionist && (
                                    <>
                                        <Link
                                            href="/rcv2"
                                            onClick={
                                                closeMenu
                                            }
                                            aria-label="recepciós felület"
                                            title="recepciós felület"
                                            className="
                                                flex h-10 w-10
                                                shrink-0
                                                cursor-pointer
                                                items-center
                                                justify-center
                                                rounded-full
                                                border-2
                                                border-[var(--brand-primary)]
                                                text-[var(--brand-primary)]
                                                transition
                                                duration-200
                                                hover:bg-[var(--brand-primary)]
                                                hover:text-[var(--brand-text-on-primary)]
                                                active:scale-95
                                                sm:h-11 sm:w-11
                                                md:h-12 md:w-12
                                            "
                                        >
                                            <QrCode
                                                className="
                                                    h-4 w-4
                                                    sm:h-5 sm:w-5
                                                    md:h-6 md:w-6
                                                "
                                            />
                                        </Link>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                closeMenu();

                                                const url =
                                                    new URL(
                                                        "http://192.168.254.5:8080/admin/"
                                                    );

                                                url.searchParams.set(
                                                    "return",
                                                    window.location.href
                                                );

                                                window.location.href =
                                                    url.toString();
                                            }}
                                            aria-label="játék kezelése"
                                            title="játék kezelése"
                                            className={`
                                                flex h-10 w-10
                                                shrink-0 cursor-pointer
                                                items-center justify-center
                                                rounded-[6px]
                                                border-2
                                                transition duration-200
                                                hover:text-[var(--brand-text-on-primary)]
                                                active:scale-95
                                                sm:h-11 sm:w-11
                                                md:h-auto md:w-auto
                                                md:min-h-[48px]
                                                md:px-5
                                                md:text-[14px]
                                                md:font-black
                                                md:tracking-[0.06em]
                                                ${isGameServerAvailable
                                                    ? "border-green-500 text-green-500 hover:bg-green-500"
                                                    : "border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]"
                                                }
                                            `}
                                        >
                                            <Gamepad2
                                                className="
                                                    h-4 w-4
                                                    sm:h-5 sm:w-5
                                                    md:hidden
                                                "
                                            />

                                            <span className="hidden md:inline">
                                                játék kezelése
                                            </span>
                                        </button>
                                    </>
                                )}
                        </div>

                        <StrikeAuthMenu
                            onOpen={() => {
                                setIsMenuOpen(
                                    false
                                );

                                setIsLanguageOpen(
                                    false
                                );
                            }}
                        />

                        <button
                            type="button"
                            onClick={
                                openLanguageMenu
                            }
                            disabled={
                                !isLanguageReady
                            }
                            aria-label={
                                language === "hu"
                                    ? "nyelv kiválasztása"
                                    : "choose language"
                            }
                            title={
                                language === "hu"
                                    ? "nyelv"
                                    : "language"
                            }
                            className="
                                flex h-10 w-11 min-[390px]:w-[52px]
                                shrink-0
                                cursor-pointer
                                items-center
                                justify-center
                                border
                                border-white/10
                                bg-white/[0.04]
                                p-1.5
                                text-white
                                transition
                                duration-200
                                hover:border-[var(--brand-primary)]
                                hover:bg-white/[0.08]
                                active:scale-95
                                disabled:cursor-wait
                                disabled:opacity-50
                                sm:h-11 sm:w-[56px]
                                md:h-12 md:w-[60px]
                            "
                        >
                            <img
                                src={
                                    language === "hu"
                                        ? "/flags/hu.svg"
                                        : "/flags/gb.svg"
                                }
                                alt=""
                                aria-hidden="true"
                                className="
                                    h-full
                                    w-full
                                    object-cover
                                    opacity-80
                                "
                            />
                        </button>

                        <button
                            type="button"
                            aria-label={
                                isMenuOpen
                                    ? "menü bezárása"
                                    : "menü megnyitása"
                            }
                            aria-expanded={
                                isMenuOpen
                            }
                            onClick={() => {
                                setIsLanguageOpen(
                                    false
                                );

                                setIsMenuOpen(
                                    (
                                        current
                                    ) =>
                                        !current
                                );
                            }}
                            className="
                                relative flex
                                h-10 w-10
                                shrink-0
                                cursor-pointer
                                flex-col
                                items-center
                                justify-center
                                gap-[5px]
                                select-none
                                [-webkit-touch-callout:none]
                                sm:h-11 sm:w-11
                                sm:gap-[6px]
                            "
                        >
                            <span
                                className={`
                                    block h-[4px]
                                    w-[27px]
                                    bg-white
                                    transition-transform
                                    duration-300
                                    ease-in-out
                                    sm:h-[5px]
                                    sm:w-[31px]
                                    ${isMenuOpen
                                        ? "translate-y-[9px] rotate-45 sm:translate-y-[11px]"
                                        : ""
                                    }
                                `}
                            />

                            <span
                                className={`
                                    block h-[4px]
                                    w-[27px]
                                    bg-white
                                    transition-all
                                    duration-300
                                    ease-in-out
                                    sm:h-[5px]
                                    sm:w-[31px]
                                    ${isMenuOpen
                                        ? "opacity-0"
                                        : "opacity-100"
                                    }
                                `}
                            />

                            <span
                                className={`
                                    block h-[4px]
                                    w-[27px]
                                    bg-white
                                    transition-transform
                                    duration-300
                                    ease-in-out
                                    sm:h-[5px]
                                    sm:w-[31px]
                                    ${isMenuOpen
                                        ? "-translate-y-[9px] -rotate-45 sm:-translate-y-[11px]"
                                        : ""
                                    }
                                `}
                            />
                        </button>
                    </div>
                </div>

                <div
                    className={`
                        absolute left-0 top-full
                        z-[100] w-full
                        overflow-y-auto overscroll-contain
                        bg-[#181818]
                        transition-all
                        duration-300
                        ease-in-out
                        ${isMenuOpen
                            ? "visible max-h-[calc(100dvh-106px)] opacity-100 md:max-h-[calc(100dvh-120px)]"
                            : "invisible pointer-events-none max-h-0 opacity-0"
                        }
                    `}
                >
                    <MainMenu
                        variant="header"
                        onItemClick={
                            closeMenu
                        }
                    />
                </div>
            </header>

            {isLanguageOpen && (
                <div
                    style={brandStyle}
                    className="
                        fixed inset-0
                        z-[999999]
                        flex items-end
                        bg-black/70
                        backdrop-blur-sm
                        md:items-center
                        md:justify-center
                        md:p-6
                    "
                    onClick={() => {
                        setIsLanguageOpen(
                            false
                        );
                    }}
                >
                    <div
                        className="
                            relative
                            w-full
                            border-t-2
                            border-[var(--brand-primary)]
                            bg-[var(--brand-background)]
                            px-5
                            pb-[calc(24px+env(safe-area-inset-bottom))]
                            pt-5
                            text-white
                            shadow-[0_-25px_80px_rgba(0,0,0,0.75)]
                            md:max-w-[440px]
                            md:border-2
                            md:border-[var(--brand-primary)]
                            md:px-7
                            md:pb-7
                            md:pt-7
                            md:[box-shadow:12px_12px_0_0_var(--brand-primary)]
                        "
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                setIsLanguageOpen(
                                    false
                                );
                            }}
                            aria-label={
                                language === "hu"
                                    ? "Nyelvválasztó bezárása"
                                    : "Close language selector"
                            }
                            className="
                                absolute
                                right-4 top-4
                                flex h-10 w-10
                                cursor-pointer
                                items-center
                                justify-center
                                rounded-full
                                border
                                border-white/10
                                text-white/60
                                transition
                                hover:border-[var(--brand-primary)]
                                hover:text-[var(--brand-primary)]
                                active:scale-95
                            "
                        >
                            <X
                                size={21}
                                strokeWidth={2.4}
                            />
                        </button>

                        <div
                            className="
                                flex
                                items-center
                                gap-4
                                pr-12
                            "
                        >
                            <div
                                className="
                                    flex h-12 w-[68px]
                                    shrink-0
                                    items-center
                                    justify-center
                                    border
                                    border-white/10
                                    bg-white/[0.04]
                                    p-2
                                "
                            >
                                <img
                                    src={
                                        language === "hu"
                                            ? "/flags/hu.svg"
                                            : "/flags/gb.svg"
                                    }
                                    alt=""
                                    aria-hidden="true"
                                    className="
                                        h-full
                                        w-full
                                        object-cover
                                        shadow-md
                                    "
                                />
                            </div>

                            <div className="min-w-0">
                                <p
                                    className="
                                        text-[10px]
                                        font-black
                                        uppercase
                                        tracking-[0.16em]
                                        text-white/40
                                    "
                                >
                                    {brandConfig.name}
                                </p>

                                <h2
                                    className="
                                        mt-1
                                        text-[19px]
                                        font-black
                                        lowercase
                                        text-white
                                        md:text-[22px]
                                    "
                                >
                                    {languageTitle}
                                </h2>
                            </div>
                        </div>

                        <p
                            className="
                                mt-5
                                max-w-[350px]
                                text-[13px]
                                leading-[1.45]
                                text-white/50
                                md:text-[14px]
                            "
                        >
                            {languageSubtitle}
                        </p>

                        <div
                            className="
                                mt-5
                                space-y-3
                                border-t
                                border-white/10
                                pt-4
                            "
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    selectLanguage(
                                        "hu"
                                    );
                                }}
                                className={`
                                    flex min-h-[58px]
                                    w-full
                                    cursor-pointer
                                    items-center
                                    justify-between
                                    gap-4
                                    border
                                    px-4
                                    text-left
                                    transition
                                    active:scale-[0.98]
                                    ${language === "hu"
                                        ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-[var(--brand-text-on-primary)]"
                                        : "border-white/10 bg-white/[0.04] text-white hover:border-[var(--brand-primary)]"
                                    }
                                `}
                            >
                                <div
                                    className="
                                        flex
                                        items-center
                                        gap-4
                                    "
                                >
                                    <span
                                        className="
                                            flex h-9 w-[50px]
                                            shrink-0
                                            items-center
                                            justify-center
                                            border
                                            border-current/20
                                            bg-black/10
                                            p-1.5
                                        "
                                    >
                                        <img
                                            src="/flags/hu.svg"
                                            alt=""
                                            aria-hidden="true"
                                            className="
                                                h-full
                                                w-full
                                                object-cover
                                                shadow-sm
                                            "
                                        />
                                    </span>

                                    <span
                                        className="
                                            text-[15px]
                                            font-black
                                        "
                                    >
                                        {hungarianLabel}
                                    </span>
                                </div>

                                {language === "hu" && (
                                    <Check
                                        size={21}
                                        strokeWidth={3}
                                    />
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    selectLanguage(
                                        "en"
                                    );
                                }}
                                className={`
                                    flex min-h-[58px]
                                    w-full
                                    cursor-pointer
                                    items-center
                                    justify-between
                                    gap-4
                                    border
                                    px-4
                                    text-left
                                    transition
                                    active:scale-[0.98]
                                    ${language === "en"
                                        ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-[var(--brand-text-on-primary)]"
                                        : "border-white/10 bg-white/[0.04] text-white hover:border-[var(--brand-primary)]"
                                    }
                                `}
                            >
                                <div
                                    className="
                                        flex
                                        items-center
                                        gap-4
                                    "
                                >
                                    <span
                                        className="
                                            flex h-9 w-[50px]
                                            shrink-0
                                            items-center
                                            justify-center
                                            border
                                            border-current/20
                                            bg-black/10
                                            p-1.5
                                        "
                                    >
                                        <img
                                            src="/flags/gb.svg"
                                            alt=""
                                            aria-hidden="true"
                                            className="
                                                h-full
                                                w-full
                                                object-cover
                                                shadow-sm
                                            "
                                        />
                                    </span>

                                    <span
                                        className="
                                            text-[15px]
                                            font-black
                                        "
                                    >
                                        {englishLabel}
                                    </span>
                                </div>

                                {language === "en" && (
                                    <Check
                                        size={21}
                                        strokeWidth={3}
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}